import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../services/api';
import styles from './LeadCampaignList.module.css';
import { usePermission } from '../../hooks/usePermissions';
import FullscreenModal from '../../components/common/FullscreenModal/FullscreenModal';
import Button from '../../components/common/Button/Button';
import AddLeadCampaign from './AddLeadCampaign';
import {
    ChevronLeftIcon, ChevronRightIcon, ReloadIcon,
    IdCardIcon, LayoutIcon, MixIcon, PersonIcon,
    BarChartIcon, CalendarIcon, ActivityLogIcon, GearIcon
} from '@radix-ui/react-icons';
import { formatLocalDateTime } from '../../utils/datetime';

// Pretty label for a stored slug — "home_top_services" → "Home Top
// Services". Display only; the row's real value is never mutated.
const titleCase = (v) => String(v || '')
    .split(/[_\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const LeadCampaignList = () => {
    const { hasPermission } = usePermission();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    // The full row for the campaign being edited, passed straight into the modal
    // so it opens instantly with no re-fetch (the list already has every field).
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [rerunId, setRerunId] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;

    // Sorting
    const [sortKey, setSortKey] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');

    // Filters
    const [searchTerm, setSearchTerm] = useState('');

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortOrder('asc');
        }
    };

    const fetchCampaigns = (page = currentPage) => {
        setLoading(true);
        api.get('/lead-campaigns')
            .then((response) => {
                let resultsArray = response?.data || response?.result || response || [];
                if (!Array.isArray(resultsArray)) resultsArray = [];

                let filtered = [...resultsArray];
                if (searchTerm) {
                    const lSearch = searchTerm.toLowerCase();
                    filtered = filtered.filter((item) => {
                        const hasPlacement = item.placement_name && item.placement_name.toLowerCase().includes(lSearch);
                        const hasSponsor = item.sponsor_name && item.sponsor_name.toLowerCase().includes(lSearch);
                        const hasId = item.id && item.id.toString().includes(lSearch);
                        return hasPlacement || hasSponsor || hasId;
                    });
                }

                filtered.sort((a, b) => {
                    let aVal = a[sortKey];
                    let bVal = b[sortKey];
                    if (aVal === null || aVal === undefined) aVal = '';
                    if (bVal === null || bVal === undefined) bVal = '';
                    const aNum = Number(aVal);
                    const bNum = Number(bVal);
                    if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && aVal !== '' && bVal !== '') {
                        return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
                    }
                    return sortOrder === 'asc'
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal));
                });

                const totalFilteredPages = Math.ceil(filtered.length / itemsPerPage) || 1;
                let safePage = page;
                if (safePage > totalFilteredPages) {
                    safePage = totalFilteredPages;
                    setCurrentPage(safePage);
                }
                const startIndex = (safePage - 1) * itemsPerPage;
                const sliced = filtered.slice(startIndex, startIndex + itemsPerPage);

                setCampaigns(sliced);
                setTotalPages(totalFilteredPages);
            })
            .catch((error) => {
                console.error('Error fetching campaigns:', error);
                toast.error('Failed to fetch campaigns');
                setCampaigns([]);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchCampaigns(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sortKey, sortOrder]);

    const handleSearch = () => {
        setCurrentPage(1);
        fetchCampaigns(1);
    };

    // Opens the confirm dialog; the actual delete runs from confirmDelete.
    const handleDelete = (id) => setDeleteId(id);

    const confirmDelete = () => {
        if (deleteId == null) return;
        setDeleting(true);
        api.delete(`/lead-campaigns/${deleteId}`)
            .then(() => {
                toast.success('Campaign deleted successfully');
                setDeleteId(null);
                fetchCampaigns(currentPage);
            })
            .catch((error) => {
                const errMsg = error?.data?.message || error?.message || 'Failed to delete';
                toast.error(`Delete Error: ${errMsg}`);
            })
            .finally(() => setDeleting(false));
    };

    // Relaunch a stopped/expired campaign: the backend re-activates it, stamps a
    // fresh activation time, and clears any past deactivation so it stays on.
    const handleRerun = (id) => {
        setRerunId(id);
        api.post(`/lead-campaigns/${id}/rerun`)
            .then(() => {
                toast.success('Campaign relaunched');
                fetchCampaigns(currentPage);
            })
            .catch((error) => {
                const errMsg = error?.data?.message || error?.message || 'Failed to rerun';
                toast.error(`Rerun Error: ${errMsg}`);
            })
            .finally(() => setRerunId(null));
    };

    const formatDate = (dateStr) => formatLocalDateTime(dateStr, 'N/A');

    return (
        <div className={styles.container}>
            <ToastContainer position="bottom-right" autoClose={2000} />

            <div className={styles.pageHeader}>
                <h1 className={styles.title}>Lead Campaigns</h1>
                {hasPermission("CREATE_NOTIFICATIONS") && (
                    <button className={styles.addBtn} onClick={() => {
                        setSelectedId(null);
                        setSelectedCampaign(null);
                        setIsModalOpen(true);
                    }}>
                        + Add Campaign
                    </button>
                )}
            </div>

            <div className={styles.card}>
                <div className={styles.filtersBar}>
                    <div className={styles.searchGroup}>
                        <label className={styles.dateLabel}>Search</label>
                        <div className={styles.searchInputRow}>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search by ID, Placement, Sponsor..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <button className={styles.searchBtn} onClick={handleSearch}>
                                Search
                            </button>
                        </div>
                    </div>
                </div>

                <div className={styles.tableWrapper}>
                    {loading ? (
                        <div className={styles.loadingState}>
                            <div className={styles.spinner}></div>
                            <span>Loading campaigns...</span>
                        </div>
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('id')} className={styles.sortable}>
                                        <IdCardIcon className={styles.thIcon} /> ID {sortKey === 'id' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th onClick={() => handleSort('placement_name')} className={styles.sortable}>
                                        <LayoutIcon className={styles.thIcon} /> Placement {sortKey === 'placement_name' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th><MixIcon className={styles.thIcon} /> Type</th>
                                    <th><PersonIcon className={styles.thIcon} /> User Group</th>
                                    <th onClick={() => handleSort('priority')} className={styles.sortable}>
                                        <BarChartIcon className={styles.thIcon} /> Priority {sortKey === 'priority' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th onClick={() => handleSort('activation_timestamp')} className={styles.sortable}>
                                        <CalendarIcon className={styles.thIcon} /> Active From {sortKey === 'activation_timestamp' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th onClick={() => handleSort('deactivation_timestamp')} className={styles.sortable}>
                                        <CalendarIcon className={styles.thIcon} /> Active To {sortKey === 'deactivation_timestamp' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th onClick={() => handleSort('status')} className={styles.sortable}>
                                        <ActivityLogIcon className={styles.thIcon} /> Status {sortKey === 'status' ? <span className={styles.sortIconActive}>⇅</span> : <span className={styles.sortIcon}>⇅</span>}
                                    </th>
                                    <th><ReloadIcon className={styles.thIcon} /> Rerun</th>
                                    <th><GearIcon className={styles.thIcon} /> Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.length > 0 ? (
                                    campaigns.map((row, idx) => (
                                        <tr key={row.id ?? idx}>
                                            <td>{row.id}</td>
                                            <td>
                                                <div className={styles.nameText}>{titleCase(row.placement_name)}</div>
                                                {row.sponsor_name && (
                                                    <div className={styles.codeText}>Sponsor: {row.sponsor_name}</div>
                                                )}
                                            </td>
                                            <td>{row.creative_type ? titleCase(row.creative_type) : 'N/A'}</td>
                                            <td>{row.target_user_group || 'all'}</td>
                                            <td>{row.priority ?? '—'}</td>
                                            <td>{formatDate(row.activation_timestamp)}</td>
                                            <td>{formatDate(row.deactivation_timestamp)}</td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${row.status?.toLowerCase() === 'active'
                                                    ? styles.statusActive
                                                    : styles.statusInactive
                                                    }`}>
                                                    {(row.status || 'UNKNOWN').toUpperCase()}
                                                </span>
                                            </td>
                                            <td>
                                                {hasPermission("MODIFY_NOTIFICATIONS") && (
                                                    <button
                                                        className={`${styles.iconBtn} ${styles.rerun}`}
                                                        title="Rerun (relaunch this campaign)"
                                                        onClick={() => handleRerun(row.id)}
                                                        disabled={rerunId === row.id}
                                                    >
                                                        <ReloadIcon />
                                                    </button>
                                                )}
                                            </td>
                                            <td>
                                                <div className={styles.actionBtns}>
                                                    {hasPermission("MODIFY_NOTIFICATIONS") && (
                                                        <button
                                                            className={`${styles.iconBtn} ${styles.edit}`}
                                                            title="Edit"
                                                            onClick={() => {
                                                                setSelectedId(row.id);
                                                                setSelectedCampaign(row);
                                                                setIsModalOpen(true);
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </button>
                                                    )}
                                                    {hasPermission("DELETE_NOTIFICATIONS") && (
                                                        <button
                                                            className={`${styles.iconBtn} ${styles.delete}`}
                                                            title="Delete"
                                                            onClick={() => handleDelete(row.id)}
                                                        >
                                                            <TrashIcon />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={10} className={styles.emptyState}>
                                            No campaigns found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className={styles.pagination}>
                    <div className={styles.paginationControls}>
                        <button
                            className={styles.pageBtn}
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((p) => p - 1)}
                            aria-label="Previous Page"
                        >
                            <ChevronLeftIcon />
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                            if (
                                p === 1 ||
                                p === totalPages ||
                                (p >= currentPage - 1 && p <= currentPage + 1)
                            ) {
                                return (
                                    <button
                                        key={p}
                                        className={`${styles.pageBtn} ${currentPage === p ? styles.active : ""}`}
                                        onClick={() => setCurrentPage(p)}
                                    >
                                        {p}
                                    </button>
                                );
                            }
                            if (p === currentPage - 2 || p === currentPage + 2) {
                                return <span key={p} className={styles.paginationEllipsis}>...</span>;
                            }
                            return null;
                        })}

                        <button
                            className={styles.pageBtn}
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage((p) => p + 1)}
                            aria-label="Next Page"
                        >
                            <ChevronRightIcon />
                        </button>
                    </div>
                </div>
            </div>
            {isModalOpen && (
                <FullscreenModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={selectedId ? "Edit Campaign" : "Create New Campaign"}
                    size="fullscreen"
                    maxWidth="1000px"
                    contentHeight="82vh"
                    disableBackdropClick={true}
                >
                    <AddLeadCampaign
                        key={selectedId ?? 'new'}
                        campaignId={selectedId}
                        initialData={selectedCampaign}
                        onSubmitted={() => {
                            setIsModalOpen(false);
                            fetchCampaigns(currentPage);
                        }}
                        onCancel={() => setIsModalOpen(false)}
                    />
                </FullscreenModal>
            )}

            {deleteId !== null && (
                <div
                    className={styles.confirmOverlay}
                    onClick={() => { if (!deleting) setDeleteId(null); }}
                >
                    <div className={styles.confirmCard} onClick={(e) => e.stopPropagation()}>
                        <h2 className={styles.confirmTitle}>Delete Campaign</h2>
                        <p className={styles.confirmText}>
                            Are you sure you want to delete this campaign? This action cannot be undone.
                        </p>
                        <div className={styles.confirmActions}>
                            <Button variant="secondary" onClick={() => setDeleteId(null)} disabled={deleting}>
                                Cancel
                            </Button>
                            <Button variant="danger" onClick={confirmDelete} loading={deleting} disabled={deleting}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadCampaignList;
